import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import PageHeader from "@/components/common/PageHeader";
import AuditLogPage from "@/pages/governance/AuditLogPage";
import ImpersonationLogPage from "@/pages/governance/ImpersonationLogPage";
import PlatformAuditPage from "@/pages/governance/PlatformAuditPage";

type TabKey = "platform" | "audit" | "impersonation";

const TABS: Array<{ key: TabKey; label: string; subtitle: string }> = [
  {
    key: "platform",
    label: "Platform Actions",
    subtitle: "Review every privileged admin mutation and failed attempt.",
  },
  {
    key: "audit",
    label: "Audit Logs",
    subtitle: "Trace sensitive actions across tenants.",
  },
  {
    key: "impersonation",
    label: "Impersonation",
    subtitle: "Track all admin impersonation sessions.",
  },
];

function AuditPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = useMemo<TabKey>(() => {
    const tab = searchParams.get("tab");
    return tab === "impersonation" || tab === "audit" ? tab : "platform";
  }, [searchParams]);

  const tabMeta = TABS.find((t) => t.key === activeTab) ?? TABS[0];

  return (
    <div className="space-y-6">
      <PageHeader title="Audit" subtitle={tabMeta.subtitle} />

      <div className="flex flex-wrap items-center gap-2">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setSearchParams({ tab: tab.key })}
            className={
              activeTab === tab.key
                ? "rounded-xl bg-slate-900 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white"
                : "rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600 hover:bg-slate-50"
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "platform" ? (
        <PlatformAuditPage />
      ) : activeTab === "audit" ? (
        <AuditLogPage embedded />
      ) : (
        <ImpersonationLogPage embedded />
      )}
    </div>
  );
}

export default AuditPage;
